import { Form, FormGroup } from '@rxdi/forms';
import { classMap, Component, css, html, LitElement, property, styleMap, TemplateResult } from '@rxdi/lit-html';
import { GraphOptions } from '@rxdi/ui-kit/graph';
import { InputStyle } from '@rxdi/ui-kit/styles/form/input';
import Fuse from 'fuse.js';
import { debounceTime, distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';

const style = css`
  ${InputStyle}

  .option {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 48px;
    height: 48px;
    padding: 0 16px;
    text-align: left;
    text-decoration: none;
    position: relative;
    cursor: pointer;
    outline: none;
    display: flex;
    flex-direction: row;
    max-width: 100%;
    box-sizing: border-box;
    align-items: center;
    user-select: none;
    -webkit-tap-highlight-color: transparent;
  }

  .option:focus {
    background: rgba(0, 0, 0, 0.04);
  }

  .option:hover {
    background: rgba(0, 0, 0, 0.04);
  }

  .panel {
    min-width: 112px;
    max-width: 280px;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
    visibility: hidden;
    max-width: none;
    max-height: 256px;
    position: relative;
    width: 100%;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
    box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12);

    position: absolute;
    background: white;
    z-index: 1000;
  }
  .visible {
    visibility: visible;
  }
`;

export type SearchResult<T = unknown> = Fuse.FuseResult<T>;

/* https://fusejs.io/examples.html#extended-search */
/**
 * @customElement gql-autocomplete
 */
@Component({
  selector: 'gql-autocomplete',
  style,
  template(this: SearchComponent) {
    return html`
      <form name="my-form" novalidate>
        <input name="text" autocomplete="off" class="rx-input" type="text" placeholder="Search" required autofocus />
        <rx-graph
          .options=${{
            style,
            loading: () => html``,
            state: this.form.valueChanges.pipe(
              debounceTime(this.debounce),
              map((values) => values.text),
              distinctUntilChanged(),
              filter((v) => v.length > 3),
              startWith(''),
              map((detail) => {
                return new Fuse(
                  this.collection,
                  this.options,
                  Fuse.createIndex(this.options.keys, this.collection)
                ).search(detail);
              })
            ),
            render: (v) => html`
              <div style=${styleMap({ width: this.width })} class=${classMap({ panel: true, visible: v.length })}>
                ${v.map(
                  (i) => html`
                    <rx-animation
                      .options=${({ stagger }) => ({
                        delay: stagger(300),
                        translateY: 0,
                        easing: 'spring(0, 30, 10, 0)',
                      })}
                    >
                      <div style="transform: translateY(-100%);" class="option">${this.template(i)}</div>
                    </rx-animation>
                  `
                )}
              </div>
            `,
          } as GraphOptions<SearchResult[]>}
        ></rx-graph>
      </form>
    `;
  },
})
export class SearchComponent extends LitElement {
  @Form({
    name: 'my-form',
    strategy: 'input',
  })
  private form = new FormGroup({
    text: '',
  });

  @property({ type: Object })
  template: (i) => TemplateResult = () => html``;

  @property({ type: String })
  width = '100%';

  @property({ type: Object })
  options: Fuse.IFuseOptions<unknown> = {
    includeScore: true,

    keys: [],
  };

  @property({ type: Number })
  debounce = 300;

  @property({ type: Array })
  collection = [];
}
